import cx from 'classnames';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

/** The native indicator is made transparent in place, not covered, so clicking it still opens the picker. */
// Firefox gives no way to hide its own calendar button, so ours is dropped there — see `date-field-icon`.
export const DateField = ({ className, ...props }: React.ComponentProps<typeof Input>): ReactElement => (
  <div className="relative w-full sm:w-fit">
    <Input
      type="date"
      className={cx(
        // Tighter on a phone, where two of these share a row and the year was cut off.
        'w-full bg-slate-50 pr-8 max-sm:pr-6 max-sm:pl-2 sm:w-auto dark:bg-slate-800',
        '[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-8 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0',
        className,
      )}
      {...props}
    />
    <CalendarDays
      data-slot="date-field-icon"
      className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground max-sm:right-2 max-sm:size-3.5"
      aria-hidden
    />
  </div>
);

/** Clear button for a date range filter; animates in when it appears. */
export const ClearFilterButton = ({
  onClick,
  className,
  /** Overrides "Clear filter" where the controls aren't filters (e.g. the export tab's selections). */
  label,
}: {
  onClick: () => void;
  className?: string;
  label?: string;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        // Same 32px box as the inputs and checkboxes it sits beside, so every filter row lines its
        // text up on one baseline. Padding-only nudging left it 2px high against a checkbox.
        'flex h-8 items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
        'duration-200 animate-in fade-in zoom-in-95 slide-in-from-left-1',
        className,
      )}
    >
      {label ?? t('clearFilter')}
    </button>
  );
};

export interface DateRangeFilterProps {
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  /** Highlight the empty field as required (e.g. when only one bound is set). */
  fromMissing?: boolean;
  toMissing?: boolean;
  /** Prefix for the input ids so multiple filters can coexist on one page. */
  idPrefix?: string;
  /** Suppress the built-in Clear button so the parent can place it elsewhere (e.g. after other filters). */
  hideClear?: boolean;
  className?: string;
}

/**
 * From/To native date range filter with a Clear button — shared by client and sprint reporting.
 *
 * A half-filled range filters nothing, so the empty end is marked and said out loud: the ring alone is
 * quiet, and a screen reader reads nothing at all from a border colour.
 */
export const DateRangeFilter = ({
  from,
  to,
  onFromChange,
  onToChange,
  fromMissing,
  toMissing,
  idPrefix = 'reports',
  hideClear,
  className,
}: DateRangeFilterProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <div className={cx('flex flex-wrap items-end gap-3 max-sm:w-full max-sm:gap-2', className)}>
      {/* Side by side on a phone too, sharing the row, rather than a line each. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-none">
        <Label htmlFor={`${idPrefix}-from`}>{t('from')}</Label>
        <DateField
          id={`${idPrefix}-from`}
          value={from}
          max={to || undefined}
          onChange={e => onFromChange(e.target.value)}
          aria-describedby={fromMissing ? `${idPrefix}-from-missing` : undefined}
          className={cx('bg-slate-50 dark:bg-slate-800', fromMissing && 'border-primary ring-2 ring-primary/50')}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-none">
        <Label htmlFor={`${idPrefix}-to`}>{t('to')}</Label>
        <DateField
          id={`${idPrefix}-to`}
          value={to}
          min={from || undefined}
          onChange={e => onToChange(e.target.value)}
          aria-describedby={toMissing ? `${idPrefix}-to-missing` : undefined}
          className={cx('bg-slate-50 dark:bg-slate-800', toMissing && 'border-primary ring-2 ring-primary/50')}
        />
      </div>
      {(fromMissing || toMissing) && (
        <p
          id={`${idPrefix}-${fromMissing ? 'from' : 'to'}-missing`}
          role="status"
          className="pb-2 text-sm text-muted-foreground"
        >
          {fromMissing ? t('pickAStartDate') : t('pickAnEndDate')}
        </p>
      )}
      {/* A range only filters once BOTH bounds are set, so a half-filled range shows no clear button. */}
      {!hideClear && from && to && (
        <ClearFilterButton
          onClick={() => {
            onFromChange('');
            onToChange('');
          }}
        />
      )}
    </div>
  );
};

export default DateRangeFilter;
